import React, { useEffect, useMemo, useRef, useState } from "react";
import { Navigate, Route, Routes, useLocation } from "react-router-dom";
import routes from "../../routes";
import { Grid } from "@mui/material";
import { ThemeProvider, createTheme } from "@mui/material/styles";
import { setPageTitle, getPageTitleFromRoute, updateCompanyNameFromToken } from "../../utils/pageTitle";
import { attachSystemColorSchemeListener } from "../../utils/adminThemePreference";
// LIMO: the product vertical. Pre-auth there is no token, so useProduct()
// resolves it from the HOST — which is exactly what the login page needs.
import { useProduct } from "../../contexts/ProductContext";
import useLimoTheme from "../../hooks/useLimoTheme";
import { applyColorScheme } from "../../utils/colorScheme";

const Auth = () => {
  const mainContent = useRef(null);
  const location = useLocation();
  const { isLimo } = useProduct();

  /* JOB-6804: this used to be the ONE shell that set the dark body class
     without a matching MUI palette. Admin.jsx:179 and StaffMember.jsx:130
     both do `mode: darkMode ? "dark" : "light"`; the theme /auth renders
     under is the global one in main.jsx, which declares a palette but no
     `mode`. So the page painted dark while MUI kept handing out light-mode
     defaults — the signup password-visibility icons took MUI's light
     `action.active`, rgba(0,0,0,0.54), and sat on the dark card at 1.24:1.

     The same value now drives both signals, so they cannot drift apart
     again. */
  const [systemDark, setSystemDark] = useState(() =>
    typeof window !== "undefined" && typeof window.matchMedia === "function"
      ? window.matchMedia("(prefers-color-scheme: dark)").matches
      : false
  );

  /* External pages always follow OS/browser theme only — not saved profile Light/Dark. */
  useEffect(() => {
    const applyAuthAreaTheme = (isSystemDark) => {
      setSystemDark(isSystemDark);
      if (isSystemDark) {
        document.body.classList.add("admin-dark-active");
      } else {
        document.body.classList.remove("admin-dark-active");
      }
      // LIMO-205: the login area follows the OS theme, so <html> has to follow
      // it too — otherwise a dark sign-in page renders light scrollbars and
      // light native controls.
      applyColorScheme(isSystemDark);
    };
    applyAuthAreaTheme(window.matchMedia("(prefers-color-scheme: dark)").matches);
    return attachSystemColorSchemeListener(applyAuthAreaTheme);
  }, []);

  /* Extends the global theme rather than replacing it. A nested
     ThemeProvider given a plain object REPLACES the outer theme (which is
     why Admin.jsx has to restate its typography), and doing that here would
     silently drop main.jsx's Inter/Poppins from every signed-out page. The
     function form deep-merges, so only the palette changes.

     The dark values are stated explicitly rather than left to `mode` alone:
     merging onto an already-built theme does not re-derive text / background
     / divider / action, and `action.active` is the exact value this ticket is
     about. They match Admin.jsx's dark palette so the signed-out and
     signed-in shells agree. */
  const authTheme = useMemo(
    () => (outerTheme) =>
      createTheme(outerTheme, {
        palette: {
          mode: systemDark ? "dark" : "light",
          ...(systemDark
            ? {
                primary: { main: "#90caf9" },
                text: { primary: "#e8eaed", secondary: "#9aa0a6" },
                divider: "#2d3b4e",
                action: {
                  active: "#e8eaed",
                  disabled: "rgba(232, 234, 237, 0.38)",
                  disabledBackground: "rgba(232, 234, 237, 0.12)",
                },
                /* `background` is deliberately NOT set. The signed-out pages
                   already get their surfaces from the stylesheets keyed on
                   body.admin-dark-active — measured with this whole block
                   stashed, the page background is rgb(15,20,25) either way —
                   so putting background.default in the palette would add a
                   second source of truth for something already handled. Only
                   the values components actually read off the theme are set
                   here. */
              }
            : {}),
        },
      }),
    [systemDark]
  );

  // LIMO: the brand palette on the signed-out pages too. Without this the login,
  // signup and password screens render Cloud Job Manager's navy on the limo host
  // — the one moment a customer sees the product before anything else.
  //
  // Companion to the same effect in Admin.jsx. It lives on <body> rather than a
  // wrapper because index.css keys the palette off `body.admin-limo-mode`, and
  // because MUI portals dialogs and poppers outside this tree.
  useLimoTheme(isLimo);

  useEffect(() => {
    document.documentElement.scrollTop = 0;
    document.scrollingElement.scrollTop = 0;
    if (mainContent.current) {
      mainContent.current.scrollTop = 0;
    }
    
    const title = getPageTitleFromRoute(location.pathname);
    setPageTitle(title);
  }, [location]);

  const getRoutes = (routes) => {
    return routes?.map((prop, key) => {
      if (prop.layout === "/auth") {
        return (
          <Route path={prop?.path} element={prop?.component} key={key} exact />
        );
      }
      return null;
    });
  };

  return (
    <ThemeProvider theme={authTheme}>
      <Grid>
        <Grid className="main-content" ref={mainContent}>
          <Grid style={{ width: "100%" }}>
            <Routes>
              {getRoutes(routes)}
              <Route path="*" element={<Navigate to="/auth/login" replace />} />
            </Routes>
          </Grid>
        </Grid>
      </Grid>
    </ThemeProvider>
  );
};

export default Auth;
